import {
  ChatBubbleOutlineOutlined,
  ChatBubbleOutlined,
  FavoriteBorderOutlined,
  FavoriteOutlined,
  DeleteOutlined,
} from "@mui/icons-material";
import { Box, Divider, IconButton, Typography, useTheme, InputBase, Button, Tooltip } from "@mui/material";
import FlexBetween from "components/FlexBetween";
import Friend from "components/Friend";
import WidgetWrapper from "components/WidgetWrapper";
import { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setPost } from "state";
import { BASE_URL } from "../../config";

const PostWidget = ({
  postId,
  postUserId,
  name,
  description,
  location,
  picturePath,
  userPicturePath,
  likes,
  comments,
  hideActions = false, // Hide friend/defriend on profile pages
}) => {
  const [isComments, setIsComments] = useState(true);
  const [showAllComments, setShowAllComments] = useState(false);
  const [commentText, setCommentText] = useState("");
  const dispatch = useDispatch();
  const token = useSelector((state) => state.token);
  const loggedInUser = useSelector((state) => state.user);
  const isAuth = Boolean(token);
  const loggedInUserId = loggedInUser?._id;
  const isLiked = loggedInUserId ? Boolean(likes[loggedInUserId]) : false;
  const likeCount = Object.keys(likes).length;

  const { palette } = useTheme();
  const main = palette.neutral.main;
  const primary = palette.primary.main;
  const medium = palette.neutral.medium;

  const patchLike = async () => {
    if (!isAuth) return; // Prevent action if not authenticated

    const response = await fetch(`${BASE_URL}/posts/${postId}/like`, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ userId: loggedInUserId }),
    });
    const updatedPost = await response.json();
    dispatch(setPost({ post: updatedPost }));
  };

  const handleComment = async () => {
    if (!commentText.trim() || !isAuth) return;

    const response = await fetch(`${BASE_URL}/posts/${postId}/comment`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ userId: loggedInUserId, comment: commentText }),
    });
    const updatedPost = await response.json();
    dispatch(setPost({ post: updatedPost }));
    setCommentText("");
  };

  const handleDeleteComment = async (comment) => {
    const response = await fetch(`${BASE_URL}/posts/${postId}/comment`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ userId: loggedInUserId, comment: comment.comment }),
    });
    const updatedPost = await response.json();
    dispatch(setPost({ post: updatedPost }));
  };

  const sortedComments = useMemo(() => {
    const myComments = comments.filter(c => c.userId === loggedInUserId);
    const otherComments = comments.filter(c => c.userId !== loggedInUserId);

    // Shuffle other comments using Fisher-Yates
    for (let i = otherComments.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [otherComments[i], otherComments[j]] = [otherComments[j], otherComments[i]];
    }

    return [...myComments, ...otherComments];
  }, [comments, loggedInUserId]);



  return (
    <WidgetWrapper>
      <Friend
        friendId={postUserId}
        name={name}
        subtitle={location}
        userPicturePath={userPicturePath}
        hideActions={hideActions}
      />
      <Typography color={main} sx={{ mt: "1rem" }}>
        {description}
      </Typography>
      {picturePath && (
        <img
          width="100%"
          height="auto"
          alt="post"
          style={{ borderRadius: "0.75rem", marginTop: "0.75rem" }}
          src={`${picturePath}`}
        />
      )}
      <FlexBetween mt="0.25rem">
        <FlexBetween gap="1rem">
          <FlexBetween gap="0.3rem">
            <Tooltip title={!isAuth ? "Login to like posts" : ""} arrow>
              <span>
                <IconButton onClick={patchLike} disabled={!isAuth}>
                  {isLiked ? (
                    <FavoriteOutlined sx={{ color: primary }} />
                  ) : (
                    <FavoriteBorderOutlined sx={{ color: !isAuth ? medium : "inherit" }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Typography>{likeCount}</Typography>
          </FlexBetween>

          <FlexBetween gap="0.3rem">
            <IconButton onClick={() => setIsComments(!isComments)}>
              {isComments ? (
                <ChatBubbleOutlined />
              ) : (
                <ChatBubbleOutlineOutlined />
              )}
            </IconButton>
            <Typography>{comments.length}</Typography>
          </FlexBetween>
        </FlexBetween>

        {/* <IconButton>
          <ShareOutlined />
        </IconButton> */}
      </FlexBetween>
      {isComments && (
        <Box mt="0.5rem">
          {(showAllComments ? sortedComments : sortedComments.slice(0, 1)).map((comment, i) => (
            <Box key={`${postId}-comment-${comment.userId}-${i}`}>
              <Divider />
              <Box sx={{ py: "0.5rem" }}>
                <FlexBetween>
                  <Box sx={{ flex: 1 }}>
                    <Friend
                      friendId={comment.userId}
                      name={`${comment.firstName} ${comment.lastName}`}
                      subtitle={comment.comment}
                      userPicturePath={comment.userPicturePath}
                      hideActions={true}
                      size="45px"
                    />
                  </Box>
                  {comment.userId === loggedInUserId && (
                    <IconButton
                      onClick={() => handleDeleteComment(comment)}
                      sx={{
                        p: "0.5rem",
                        "&:hover": {
                          color: palette.error.main,
                        }
                      }}
                    >
                      <DeleteOutlined fontSize="small" />
                    </IconButton>
                  )}
                </FlexBetween>
              </Box>
            </Box>
          ))}

          {/* View More / View Less Button */}
          {comments.length > 1 && (
            <Box textAlign="center" mb="0.5rem">
              <Button
                onClick={() => setShowAllComments(!showAllComments)}
                size="small"
                sx={{
                  color: primary,
                  textTransform: "none",
                  fontSize: "0.85rem",
                  fontWeight: 500,
                  "&:hover": {
                    backgroundColor: "rgba(0, 191, 255, 0.08)",
                  },
                }}
              >
                {showAllComments ? "View less" : `View ${comments.length - 1} more comment${comments.length - 1 > 1 ? 's' : ''}`}
              </Button>
            </Box>
          )}

          <Divider />
          {/* Only show comment input for authenticated users */}
          {isAuth ? (
            <FlexBetween gap="1rem" mt="1rem">
              <InputBase
                placeholder="Write a comment..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleComment();
                  }
                }}
                sx={{
                  width: "100%",
                  backgroundColor: palette.neutral.light,
                  borderRadius: "1.5rem",
                  padding: "0.4rem 1rem",
                  fontSize: "0.85rem",
                }}
              />
              <Button
                disabled={!commentText.trim()}
                onClick={handleComment}
                size="small"
                sx={{
                  color: palette.background.alt,
                  backgroundColor: primary,
                  borderRadius: "1.5rem",
                  textTransform: "none",
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  px: 2.5,
                  py: 0.6,
                  boxShadow: "0 2px 8px rgba(0, 191, 255, 0.3)",
                  "&:hover": {
                    backgroundColor: palette.primary.light,
                    boxShadow: "0 4px 12px rgba(0, 191, 255, 0.4)",
                  },
                  "&:disabled": {
                    backgroundColor: palette.neutral.light,
                    color: palette.neutral.main,
                    boxShadow: "none",
                  },
                }}
              >
                Post
              </Button>
            </FlexBetween>
          ) : (
            <Typography
              color={medium}
              textAlign="center"
              sx={{ mt: "1rem", fontStyle: "italic" }}
            >
              Login to add comments
            </Typography>
          )}
        </Box>
      )}
    </WidgetWrapper>
  );
};

export default PostWidget;